import React, { useState } from "react";
import { useFormikContext } from "formik";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import styles from "../../style.module.scss";
import ConfirmationModal from "./../../../application/components/ConfirmationModal/ConfirmationModal";
import useUserStore from "../../../../../v2/stores/user";
import config from "../../../../../config";
import { scrollToError } from "../../utils/scroll-to-error";
import { useEquipmentsByTypeAndBrand } from "../equipments/useEquipmentsByTypeAndBrand";
import { useEMSValidation } from "../../../../hooks/useEMSValidation";
import {
  contactInfoSchema,
  createPricingInfoSchema,
  detailsInfoSchema,
  processorInfoSchema,
} from "../../validation-schema";
import useSalesFormStore from "../../../../stores/sales-form";
import useOrganizationStore from "../../../../stores/organization";
import { shouldConfirmOnlyKurvEquipment } from "../../utils/kurv-equipment-confirmation";

const Footer = ({ setSubmissionType, dataLoading, setOpenSections }) => {
  const {
    values,
    errors,
    touched,
    setFieldError,
    setFieldTouched,
    handleSubmit,
    isSubmitting,
    validateForm,
    setFieldValue,
    setTouched,
  } = useFormikContext();

  const [confirmId, setConfirmId] = useState(null);
  const [buttonText, setButtonText] = useState("Save");
  const organizationId = useUserStore((state) => state.organizationId);
  const isPCUser = useUserStore((state) => state.isPCUser);
  const isKurvUser = useUserStore((state) => state.isKurvUser);
  const { validatePhone } = useEMSValidation();
  const validationStatuses = useSalesFormStore((s) => s.validationStatuses);
  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );
  const { equipments } = useEquipmentsByTypeAndBrand();

  const handleButtonClick = async (btnText) => {
    const kurvMerchant = values?.equipments.find(
      (item) => item.id === config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
    );
    const kurvProcessorSelected = values?.processors?.includes(
      config.paymentcloud.KURV_PROCESSOR_ID,
    );

    let kurvMerchantAutoAdded = false;
    let currentEquipments = values?.equipments || [];
    if ((isKurvUser || kurvProcessorSelected) && !kurvMerchant) {
      const firstMatchEquipment = equipments.find(
        (item) => item.id === config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
      );
      if (firstMatchEquipment && firstMatchEquipment?.id) {
        setFieldValue(
          "equipments",
          [
            { ...firstMatchEquipment, type: "monthly_fee", quantity: 1 },
            ...values.equipments,
          ],
          true,
        );
        currentEquipments = [
          { ...firstMatchEquipment, type: "monthly_fee", quantity: 1 },
          ...values.equipments,
        ];
        kurvMerchantAutoAdded = true;
      }
    }

    const isValidPhone = await validatePhone(
      values.mobile,
      "mobile",
      setFieldError,
      setFieldTouched,
    );

    // Note: this might seem odd here, but it's necessary to validate the form again
    // so formik/react re-renders, sections open, and scrolling to the error works.
    let allErrors = await validateForm();
    const hasContactErrors =
      !contactInfoSchema.isValidSync(values) ||
      validationStatuses?.mobile === false;
    const hasPricingErrors =
      !createPricingInfoSchema(pricingDefaults).isValidSync(values);
    const hasEquipmentErrors =
      !kurvMerchantAutoAdded &&
      (allErrors.equipments || currentEquipments.length === 0);
    const hasDetailsErrors = !detailsInfoSchema.isValidSync(values);
    const hasProcessorErrors = !processorInfoSchema.isValidSync(values);

    // mimicing formik submit behavior
    setTouched(
      Object.keys(values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
    );

    setOpenSections((prev) => ({
      ...prev,
      contact: hasContactErrors,
      pricing: hasPricingErrors,
      equipments: hasEquipmentErrors,
      details: hasDetailsErrors,
      processors: hasProcessorErrors,
    }));

    if (!isValidPhone) {
      allErrors = {
        ...allErrors,
        mobile: "* Enter a valid phone",
      };
    }

    scrollToError(allErrors);

    if (Object.keys(allErrors).length > 0 || !isValidPhone) {
      return;
    }

    const shouldShowConfirmation = shouldConfirmOnlyKurvEquipment({
      equipments: currentEquipments,
      processors: values?.processors,
      organizationId,
    });

    if (shouldShowConfirmation) {
      setButtonText(btnText);
      setConfirmId(currentEquipments[0].id);
    } else {
      handleSubmit();
    }
  };

  const sendToSubmit = () => {
    setConfirmId(null);
    handleSubmit();
  };

  return (
    <div className={styles.buttons}>
      <ButtonIcon
        size="large"
        textColor="#254FE2"
        onClick={async () => {
          setSubmissionType("create_app");
          await handleButtonClick("Save");
        }}
        disabled={isSubmitting || dataLoading}
      >
        Save
      </ButtonIcon>

      <ButtonIcon
        size="large"
        bgColor={"#EDEDF4"}
        textColor="#050506"
        onClick={async () => {
          setSubmissionType("continue");

          await handleButtonClick("Continue");
        }}
        disabled={isSubmitting || Object.keys(errors).length > 0 || dataLoading}
      >
        Continue
      </ButtonIcon>

      <ButtonIcon
        size="large"
        bgColor={"#4C70EF"}
        textColor="#FFFFFF"
        onClick={async () => {
          setSubmissionType("send_to_merchant");
          await handleButtonClick("Send to Merchant");
        }}
        disabled={isSubmitting || Object.keys(errors).length > 0 || dataLoading}
      >
        Send to Merchant
      </ButtonIcon>
      <ButtonIcon
        size="large"
        bgColor={"#4C70EF"}
        textColor="#FFFFFF"
        onClick={async () => {
          setSubmissionType("send_app_for_signature");
          await handleButtonClick("Send App for Signature");
        }}
        disabled={isSubmitting || Object.keys(errors).length > 0 || dataLoading}
      >
        Send App for Signature
      </ButtonIcon>
      <ConfirmationModal
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        message={
          "No equipment selected - Kurv Merchant applied. Please confirm."
        }
        submitButtonText={`Confirm & ${buttonText}`}
        cancelButtonText={"Go Back"}
        onConfirmClick={() => sendToSubmit()}
      />
    </div>
  );
};

export default Footer;
